/**
 * Client-side helpers for WhatsApp template send UX (18.2).
 * Mirrors Phase 1 backend discovery rules for supported v1 shapes only.
 * Backend remains authoritative for validation and provider encoding.
 */

import type {
  CreateTemplateMessageParameter,
  WhatsAppTemplatePresentation,
  WhatsAppTemplatePresentationButton,
} from "@multi-chat/shared";

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

export type TemplateSendComponentType = "HEADER" | "BODY" | "BUTTON";

export interface TemplateSendSlot {
  componentType: TemplateSendComponentType;
  parameterKey: string;
  buttonIndex?: number;
  /** Static URL template text when componentType is BUTTON (may include {{var}}). */
  urlTemplate?: string;
  buttonLabel?: string;
}

export interface TemplateSendButtonDef {
  type: WhatsAppTemplatePresentationButton["type"];
  text: string;
  url?: string;
  phoneNumber?: string;
}

export type TemplateSendDiscovery =
  | { kind: "unsupported" }
  | {
      kind: "supported";
      slots: TemplateSendSlot[];
      bodyText: string | null;
      headerText: string | null;
      headerFormat: "NONE" | "TEXT" | "IMAGE";
      footerText: string | null;
      buttons: TemplateSendButtonDef[];
    };

export function extractBodyPreview(components: unknown): string | null {
  if (!Array.isArray(components)) return null;
  for (const entry of components) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const component = entry as Record<string, unknown>;
    const type = typeof component.type === "string" ? component.type.trim().toUpperCase() : "";
    if (type !== "BODY") continue;
    if (typeof component.text !== "string") return null;
    const trimmed = component.text.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return null;
}

/**
 * Discover required send parameters from cached template components.
 * Returns unsupported for any shape Phase 1 cannot send.
 */
export function discoverTemplateSendSlots(components: unknown): TemplateSendDiscovery {
  if (!Array.isArray(components)) {
    return { kind: "unsupported" };
  }

  let bodyText: string | null = null;
  let headerText: string | null = null;
  let headerFormat: "NONE" | "TEXT" | "IMAGE" = "NONE";
  let footerText: string | null = null;
  const buttons: TemplateSendButtonDef[] = [];
  const slots: TemplateSendSlot[] = [];
  const placeholderKeys: string[] = [];

  for (const entry of components) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return { kind: "unsupported" };
    }
    const component = entry as Record<string, unknown>;
    const type = typeof component.type === "string" ? component.type.trim().toUpperCase() : "";

    if (type === "HEADER") {
      const format =
        typeof component.format === "string" ? component.format.trim().toUpperCase() : "TEXT";
      if (format === "IMAGE") {
        headerFormat = "IMAGE";
        continue;
      }
      if (format === "VIDEO" || format === "DOCUMENT" || format === "LOCATION") {
        return { kind: "unsupported" };
      }
      if (format !== "TEXT") {
        return { kind: "unsupported" };
      }
      if (typeof component.text !== "string") {
        return { kind: "unsupported" };
      }
      headerFormat = "TEXT";
      headerText = component.text;
      const keys = extractPlaceholderKeys(component.text);
      if (keys.length > 1) {
        return { kind: "unsupported" };
      }
      for (const key of keys) {
        placeholderKeys.push(key);
        slots.push({ componentType: "HEADER", parameterKey: key });
      }
      continue;
    }

    if (type === "BODY") {
      if (typeof component.text !== "string") {
        return { kind: "unsupported" };
      }
      bodyText = component.text;
      for (const key of extractPlaceholderKeys(component.text)) {
        placeholderKeys.push(key);
        slots.push({ componentType: "BODY", parameterKey: key });
      }
      continue;
    }

    if (type === "FOOTER") {
      if (typeof component.text === "string" && component.text.trim().length > 0) {
        footerText = component.text.trim();
      }
      continue;
    }

    if (type === "BUTTONS") {
      if (!Array.isArray(component.buttons)) {
        return { kind: "unsupported" };
      }
      for (const [buttonIndex, buttonEntry] of component.buttons.entries()) {
        if (!buttonEntry || typeof buttonEntry !== "object" || Array.isArray(buttonEntry)) {
          return { kind: "unsupported" };
        }
        const button = buttonEntry as Record<string, unknown>;
        const buttonType =
          typeof button.type === "string" ? button.type.trim().toUpperCase() : "";
        const buttonText = typeof button.text === "string" ? button.text : "";

        if (buttonType === "QUICK_REPLY") {
          buttons.push({ type: "QUICK_REPLY", text: buttonText });
          continue;
        }

        if (buttonType === "PHONE_NUMBER" || buttonType === "PHONE") {
          buttons.push({
            type: "PHONE_NUMBER",
            text: buttonText,
            ...(typeof button.phone_number === "string"
              ? { phoneNumber: button.phone_number }
              : typeof button.phoneNumber === "string"
                ? { phoneNumber: button.phoneNumber }
                : {}),
          });
          continue;
        }

        if (buttonType === "VOICE_CALL") {
          buttons.push({ type: "VOICE_CALL", text: buttonText });
          continue;
        }

        if (buttonType === "URL") {
          const url = typeof button.url === "string" ? button.url : "";
          buttons.push({ type: "URL", text: buttonText, ...(url ? { url } : {}) });
          const keys = extractPlaceholderKeys(url);
          if (keys.length === 0) {
            continue;
          }
          if (keys.length !== 1) {
            return { kind: "unsupported" };
          }
          const parameterKey = keys[0]!;
          placeholderKeys.push(parameterKey);
          slots.push({
            componentType: "BUTTON",
            buttonIndex,
            parameterKey,
            urlTemplate: url,
            buttonLabel: buttonText || undefined,
          });
          continue;
        }

        return { kind: "unsupported" };
      }
      continue;
    }

    if (type === "") {
      return { kind: "unsupported" };
    }

    return { kind: "unsupported" };
  }

  if (!isHomogeneousParameterFormat(placeholderKeys)) {
    return { kind: "unsupported" };
  }

  return { kind: "supported", slots, bodyText, headerText, headerFormat, footerText, buttons };
}

export function slotStorageKey(slot: TemplateSendSlot): string {
  if (slot.componentType === "BUTTON") {
    return `BUTTON:${slot.buttonIndex}:${slot.parameterKey}`;
  }
  return `${slot.componentType}:${slot.parameterKey}`;
}

export function buildTemplateSendParameters(
  slots: readonly TemplateSendSlot[],
  valuesBySlotKey: ReadonlyMap<string, string>,
): CreateTemplateMessageParameter[] | { kind: "missing"; slotKey: string } {
  const parameters: CreateTemplateMessageParameter[] = [];
  for (const slot of slots) {
    const key = slotStorageKey(slot);
    const raw = valuesBySlotKey.get(key) ?? "";
    const value = raw.trim();
    if (value.length === 0) {
      return { kind: "missing", slotKey: key };
    }
    if (slot.componentType === "BUTTON") {
      parameters.push({
        componentType: "BUTTON",
        buttonIndex: slot.buttonIndex!,
        parameterKey: slot.parameterKey,
        value,
      });
    } else {
      parameters.push({
        componentType: slot.componentType,
        parameterKey: slot.parameterKey,
        value,
      });
    }
  }
  return parameters;
}

export function buildClientTemplatePreview(input: {
  templateName: string;
  headerText: string | null;
  bodyText: string | null;
  slots: readonly TemplateSendSlot[];
  valuesBySlotKey: ReadonlyMap<string, string>;
}): {
  header: string | null;
  body: string;
  urlButtons: Array<{ label: string | null; url: string }>;
} {
  const header =
    input.headerText != null
      ? substitutePlaceholders(input.headerText, "HEADER", input.slots, input.valuesBySlotKey)
      : null;

  const body =
    input.bodyText != null && input.bodyText.trim().length > 0
      ? substitutePlaceholders(input.bodyText, "BODY", input.slots, input.valuesBySlotKey)
      : `Template: ${input.templateName}`;

  const urlButtons = input.slots
    .filter((slot) => slot.componentType === "BUTTON" && slot.urlTemplate)
    .map((slot) => {
      const value = (input.valuesBySlotKey.get(slotStorageKey(slot)) ?? "").trim();
      const url = value
        ? slot.urlTemplate!.replace(
            new RegExp(`\\{\\{\\s*${escapeRegExp(slot.parameterKey)}\\s*\\}\\}`, "g"),
            value,
          )
        : slot.urlTemplate!;
      return { label: slot.buttonLabel ?? null, url };
    });

  return { header, body, urlButtons };
}

/** Full send-time presentation for shared WhatsAppTemplatePreview. */
export function buildClientTemplatePresentation(input: {
  templateName: string;
  headerText: string | null;
  headerFormat?: "NONE" | "TEXT" | "IMAGE";
  headerImageUrl?: string | null;
  headerImageMissing?: boolean;
  bodyText: string | null;
  footerText: string | null;
  buttons: readonly TemplateSendButtonDef[];
  slots: readonly TemplateSendSlot[];
  valuesBySlotKey: ReadonlyMap<string, string>;
}): WhatsAppTemplatePresentation {
  const preview = buildClientTemplatePreview(input);
  const buttons: WhatsAppTemplatePresentationButton[] = input.buttons.map(
    (button, buttonIndex) => {
      if (button.type === "URL") {
        const slot = input.slots.find(
          (entry) => entry.componentType === "BUTTON" && entry.buttonIndex === buttonIndex,
        );
        if (slot?.urlTemplate) {
          const value = (input.valuesBySlotKey.get(slotStorageKey(slot)) ?? "").trim();
          const url = value
            ? slot.urlTemplate.replace(
                new RegExp(`\\{\\{\\s*${escapeRegExp(slot.parameterKey)}\\s*\\}\\}`, "g"),
                value,
              )
            : slot.urlTemplate;
          return { type: "URL" as const, text: button.text, url };
        }
        return button.url
          ? { type: "URL" as const, text: button.text, url: button.url }
          : { type: "URL" as const, text: button.text };
      }
      if (button.type === "PHONE_NUMBER") {
        return {
          type: "PHONE_NUMBER" as const,
          text: button.text,
          ...(button.phoneNumber ? { phoneNumber: button.phoneNumber } : {}),
        };
      }
      if (button.type === "VOICE_CALL") {
        return { type: "VOICE_CALL" as const, text: button.text };
      }
      return { type: "QUICK_REPLY" as const, text: button.text };
    },
  );

  return {
    ...(input.headerFormat === "IMAGE"
      ? {
          header: {
            type: "IMAGE" as const,
            ...(input.headerImageUrl ? { imageUrl: input.headerImageUrl } : {}),
            missing: input.headerImageMissing === true || !input.headerImageUrl,
          },
        }
      : preview.header
        ? { header: { type: "TEXT" as const, text: preview.header } }
        : {}),
    body: { text: preview.body },
    ...(input.footerText ? { footer: { text: input.footerText } } : {}),
    buttons,
  };
}

export function humanizeParameterKey(parameterKey: string): string {
  if (/^\d+$/.test(parameterKey)) {
    return parameterKey;
  }
  return parameterKey
    .split("_")
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function isPositionalParameterKey(parameterKey: string): boolean {
  return /^\d+$/.test(parameterKey);
}

export type TemplateSendSlotLabelModel =
  | {
      kind: "positional";
      n: string;
      componentType: TemplateSendComponentType;
    }
  | {
      kind: "named";
      keyLabel: string;
      componentType: TemplateSendComponentType;
    };

/** Labels follow template variable semantics — positional vs named keys only. */
export function templateSendSlotLabelModel(slot: TemplateSendSlot): TemplateSendSlotLabelModel {
  if (isPositionalParameterKey(slot.parameterKey)) {
    return {
      kind: "positional",
      n: slot.parameterKey,
      componentType: slot.componentType,
    };
  }
  return {
    kind: "named",
    keyLabel: humanizeParameterKey(slot.parameterKey),
    componentType: slot.componentType,
  };
}

export function templateSlotInputDir(slot: TemplateSendSlot): "ltr" | "auto" {
  return slot.componentType === "BUTTON" ? "ltr" : "auto";
}

export function missingRequiredTemplateSlotKeys(
  slots: readonly TemplateSendSlot[],
  valuesBySlotKey: ReadonlyMap<string, string>,
): string[] {
  const missing: string[] = [];
  for (const slot of slots) {
    const key = slotStorageKey(slot);
    if ((valuesBySlotKey.get(key) ?? "").trim().length === 0) {
      missing.push(key);
    }
  }
  return missing;
}

export function hasAllRequiredTemplateParameters(
  slots: readonly TemplateSendSlot[],
  valuesBySlotKey: ReadonlyMap<string, string>,
): boolean {
  return missingRequiredTemplateSlotKeys(slots, valuesBySlotKey).length === 0;
}

/** True when the template definition includes a VOICE_CALL button (not PHONE_NUMBER). */
export function templateHasVoiceCallButton(components: unknown): boolean {
  if (!Array.isArray(components)) {
    return false;
  }
  for (const component of components) {
    if (!component || typeof component !== "object" || Array.isArray(component)) {
      continue;
    }
    const type = (component as { type?: unknown }).type;
    if (typeof type !== "string" || type.trim().toUpperCase() !== "BUTTONS") {
      continue;
    }
    const buttons = (component as { buttons?: unknown }).buttons;
    if (!Array.isArray(buttons)) {
      continue;
    }
    for (const button of buttons) {
      if (!button || typeof button !== "object" || Array.isArray(button)) {
        continue;
      }
      const buttonType = (button as { type?: unknown }).type;
      if (typeof buttonType === "string" && buttonType.trim().toUpperCase() === "VOICE_CALL") {
        return true;
      }
    }
  }
  return false;
}

function extractPlaceholderKeys(text: string): string[] {
  const keys: string[] = [];
  PLACEHOLDER_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = PLACEHOLDER_RE.exec(text)) !== null) {
    keys.push(match[1]!);
  }
  return keys;
}

function isHomogeneousParameterFormat(keys: readonly string[]): boolean {
  if (keys.length === 0) return true;
  const allPositional = keys.every((key) => /^\d+$/.test(key));
  const allNamed = keys.every((key) => !/^\d+$/.test(key));
  return allPositional || allNamed;
}

function substitutePlaceholders(
  text: string,
  componentType: "HEADER" | "BODY",
  slots: readonly TemplateSendSlot[],
  valuesBySlotKey: ReadonlyMap<string, string>,
): string {
  let preview = text;
  for (const slot of slots) {
    if (slot.componentType !== componentType) continue;
    const value = valuesBySlotKey.get(slotStorageKey(slot)) ?? "";
    const pattern = new RegExp(`\\{\\{\\s*${escapeRegExp(slot.parameterKey)}\\s*\\}\\}`, "g");
    preview = preview.replace(pattern, value.trim().length > 0 ? value.trim() : `{{${slot.parameterKey}}}`);
  }
  return preview;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
